/**
 * /admin/partners — admin dashboard for the third-party partner surface.
 *
 * Two tabs:
 *   • Applications — pending / approved / rejected. Approve mints a key.
 *   • Partners     — active keys, last-used metrics, revoke.
 *
 * Admin gated at the API layer (backend returns 403 for non-admins);
 * the page itself renders a friendly "not authorised" pane in that case.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Building2, Shield, Loader2, Check, X, Copy, ClipboardCheck, ChevronDown,
  Sparkles, KeyRound, ExternalLink, Bell,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const CATEGORIES = [
  "help_people", "save_food", "protect_nature", "support_farmers",
  "share_knowledge", "reuse_give", "volunteer_serve", "build_community",
];

const STATUS_PILL = {
  pending:  { label: "Pending",   color: "#B45309", bg: "#FEF3C7" },
  approved: { label: "Approved",  color: "#065F46", bg: "#D1FAE5" },
  rejected: { label: "Rejected",  color: "#991B1B", bg: "#FEE2E2" },
  active:   { label: "Active",    color: "#065F46", bg: "#D1FAE5" },
  revoked:  { label: "Revoked",   color: "#991B1B", bg: "#FEE2E2" },
};

export default function AdminPartnersPage() {
  const { user } = useAuth() || {};
  const [tab, setTab] = useState("applications");
  const [apps, setApps] = useState([]);
  const [appCounts, setAppCounts] = useState({});
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadAlerts, setUnreadAlerts] = useState(0);

  const loadAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [a, p, n] = await Promise.all([
        http.get("/partners/applications"),
        http.get("/partners/list"),
        http.get("/admin/notifications?only_unread=true&limit=20").catch(() => ({ data: { unread: 0 } })),
      ]);
      setApps(a.data?.items || []);
      setAppCounts(a.data?.counts || {});
      setPartners(p.data?.items || []);
      setUnreadAlerts(n.data?.unread || 0);
    } catch (e) {
      setError(e?.response?.status === 403 ? "not-admin" : "error");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const markAllRead = async () => {
    try {
      await http.post("/admin/notifications/mark-all-read");
      setUnreadAlerts(0);
      toast.success("Alerts cleared");
    } catch { /* ignore */ }
  };

  const isAdmin = user && (user.is_admin || user.platform_role === "super_admin");

  if (!user) {
    return <NotAuthorized reason="signin" />;
  }
  if (!loading && error === "not-admin") {
    return <NotAuthorized reason="admin" />;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 md:py-14" data-testid="admin-partners">
      <header className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest mb-2 inline-flex items-center gap-1.5"
             style={{ color: "#059669" }}>
          <Shield className="w-3.5 h-3.5" /> Admin · Partners
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0f172a" }}>
            Partner applications & keys
          </h1>
          {unreadAlerts > 0 && (
            <button
              onClick={markAllRead}
              data-testid="admin-partners-alerts-bell"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold animate-pulse"
              style={{ background: "#FEF3C7", color: "#B45309" }}
              title="Mark all alerts as read"
            >
              <Bell className="w-3.5 h-3.5" />
              {unreadAlerts} new · click to clear
            </button>
          )}
        </div>
        <p className="mt-3 text-sm opacity-75 max-w-2xl">
          Review self-serve applications from third-party integrators and
          issue partner keys with a scoped auto-approve policy.
        </p>
      </header>

      <nav className="flex flex-wrap gap-2 mb-6" data-testid="admin-partners-tabs">
        <TabBtn active={tab === "applications"} onClick={() => setTab("applications")}
                testid="admin-partners-tab-applications">
          Applications
          <span className="ml-2 rounded-full text-[10px] px-1.5 py-0.5"
                style={{ background: "rgba(255,255,255,0.2)" }}>
            {(appCounts.pending || 0)}
          </span>
        </TabBtn>
        <TabBtn active={tab === "partners"} onClick={() => setTab("partners")}
                testid="admin-partners-tab-partners">
          Active partners
          <span className="ml-2 rounded-full text-[10px] px-1.5 py-0.5"
                style={{ background: "rgba(255,255,255,0.2)" }}>
            {partners.filter((p) => p.status === "active").length}
          </span>
        </TabBtn>
        <button
          onClick={loadAll}
          className="ml-auto text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1"
          disabled={loading}
          data-testid="admin-partners-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh"}
        </button>
      </nav>

      {tab === "applications" ? (
        <ApplicationsList apps={apps} onChange={loadAll} isAdmin={isAdmin} />
      ) : (
        <PartnersList partners={partners} onChange={loadAll} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors"
      style={{
        background: active ? "#059669" : "#F1F5F9",
        color:      active ? "#F0FDF4" : "#0F172A",
      }}
    >
      {children}
    </button>
  );
}

function ApplicationsList({ apps, onChange, isAdmin }) {
  if (!apps.length) return <Empty label="No applications yet." />;
  return (
    <div className="space-y-3" data-testid="admin-partners-applications">
      {apps.map((a) => (
        <ApplicationCard key={a.id} app={a} onChange={onChange} isAdmin={isAdmin} />
      ))}
    </div>
  );
}

function ApplicationCard({ app, onChange, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("write:pending");
  const [auto, setAuto] = useState([]);
  const [saving, setSaving] = useState(false);
  const [mintedKey, setMintedKey] = useState("");
  const status = STATUS_PILL[app.status] || STATUS_PILL.pending;

  const approve = async () => {
    setSaving(true);
    try {
      const { data } = await http.post(`/partners/applications/${app.id}/approve`, {
        scope,
        auto_approve_categories: scope === "write:auto" ? auto : [],
      });
      setMintedKey(data?.key || "");
      toast.success("Partner approved — key minted");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border p-5 bg-white"
         style={{ borderColor: "rgba(0,0,0,0.08)" }}
         data-testid={`admin-application-${app.id}`}>
      <div className="flex items-start gap-4">
        <Building2 className="w-8 h-8" style={{ color: "#059669" }} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-semibold text-lg truncate">{app.org_name}</div>
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  style={{ background: status.bg, color: status.color }}>
              {status.label}
            </span>
            <a href={app.website} target="_blank" rel="noopener noreferrer"
               className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1"
               data-testid={`admin-application-${app.id}-website`}>
              {app.website} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="text-xs opacity-70 mt-1">
            {app.contact_name} · <a href={`mailto:${app.contact_email}`} className="underline">{app.contact_email}</a>
            {typeof app.expected_volume_per_month === "number" && (
              <> · ~{app.expected_volume_per_month.toLocaleString()} contributions/mo</>
            )}
          </div>
          <p className="text-sm opacity-85 mt-3 leading-relaxed whitespace-pre-wrap">
            {app.use_case}
          </p>
          {app.categories?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {app.categories.map((c) => (
                <span key={c} className="text-[11px] font-mono rounded-full px-2 py-0.5"
                      style={{ background: "#F1F5F9", color: "#0F172A" }}>
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>

        {app.status === "pending" && isAdmin && (
          <button onClick={() => setOpen((v) => !v)}
                  className="rounded-full px-4 py-2 text-sm font-semibold text-white"
                  style={{ background: "#059669" }}
                  data-testid={`admin-application-${app.id}-approve-btn`}>
            {open ? "Cancel" : "Approve →"}
          </button>
        )}
      </div>

      {open && !mintedKey && (
        <div className="mt-5 pt-5 border-t space-y-3" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest mb-1 opacity-70">Scope</div>
            <select value={scope} onChange={(e) => setScope(e.target.value)}
                    className="w-full sm:w-auto rounded-lg border px-3 py-2 text-sm"
                    style={{ borderColor: "rgba(0,0,0,0.15)" }}
                    data-testid={`admin-application-${app.id}-scope`}>
              <option value="write:pending">write:pending — every push needs moderator approval</option>
              <option value="write:auto">write:auto — auto-approve within listed categories</option>
            </select>
          </div>
          {scope === "write:auto" && (
            <div>
              <div className="text-xs font-mono uppercase tracking-widest mb-1 opacity-70">Auto-approve categories</div>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => {
                  const on = auto.includes(c);
                  return (
                    <button key={c} type="button"
                            onClick={() => setAuto((cur) => on ? cur.filter((x) => x !== c) : [...cur, c])}
                            className="rounded-full px-3 py-1 text-xs font-mono transition-colors"
                            style={{
                              background: on ? "#059669" : "#F1F5F9",
                              color:      on ? "#F0FDF4" : "#0F172A",
                            }}
                            data-testid={`admin-application-${app.id}-cat-${c}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <button onClick={approve} disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: "#059669" }}
                  data-testid={`admin-application-${app.id}-approve-confirm`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Approve & mint key
          </button>
        </div>
      )}

      {mintedKey && (
        <div className="mt-5 pt-5 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
          <div className="text-xs font-mono uppercase tracking-widest mb-2 inline-flex items-center gap-1.5"
               style={{ color: "#059669" }}>
            <KeyRound className="w-3 h-3" /> One-time raw key (copy now)
          </div>
          <CopyRow value={mintedKey} testid={`admin-application-${app.id}-minted`} />
          <div className="text-xs opacity-70 mt-2">
            Email this to <b>{app.contact_email}</b>. It will not be shown again.
          </div>
        </div>
      )}
    </div>
  );
}

function PartnersList({ partners, onChange }) {
  if (!partners.length) return <Empty label="No partner keys minted yet." />;
  return (
    <div className="space-y-3" data-testid="admin-partners-active">
      {partners.map((p) => (
        <PartnerRow key={p.id} p={p} onChange={onChange} />
      ))}
    </div>
  );
}

function PartnerRow({ p, onChange }) {
  const status = STATUS_PILL[p.status] || STATUS_PILL.active;
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !analytics) {
      setLoading(true);
      try {
        const { data } = await http.get(`/partners/${p.id}/analytics`);
        setAnalytics(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Analytics failed to load");
      } finally {
        setLoading(false);
      }
    }
  };
  const revoke = async () => {
    if (!window.confirm(`Revoke ${p.org_name}? This immediately invalidates their key.`)) return;
    try {
      await http.post(`/partners/${p.id}/revoke`);
      toast.success("Partner revoked");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };
  return (
    <div className="rounded-2xl border bg-white"
         style={{ borderColor: "rgba(0,0,0,0.08)" }}
         data-testid={`admin-partner-${p.id}`}>
      <div className="p-5 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-base truncate">{p.org_name}</div>
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  style={{ background: status.bg, color: status.color }}>
              {status.label}
            </span>
          </div>
          <div className="text-xs opacity-70 mt-1">
            scope · <b>{p.scope}</b>
            {p.auto_approve_categories?.length > 0 && <> · auto: {p.auto_approve_categories.join(", ")}</>}
          </div>
          <div className="text-xs opacity-60 mt-1">
            key ends …{p.key_last4} · {p.used_count || 0} calls
            {p.last_used_at && <> · last used {(p.last_used_at).slice(0, 10)}</>}
          </div>
        </div>
        <button onClick={toggle}
                className="rounded-full px-3 py-1.5 text-xs font-semibold border inline-flex items-center gap-1"
                style={{ borderColor: "rgba(0,0,0,0.15)" }}
                data-testid={`admin-partner-${p.id}-expand`}>
          {expanded ? "Hide" : "Analytics"}
          <ChevronDown className="w-3 h-3" style={{ transform: expanded ? "rotate(180deg)" : "" }} />
        </button>
        {p.status === "active" && (
          <button onClick={revoke}
                  className="rounded-full px-4 py-2 text-sm font-semibold border"
                  style={{ borderColor: "#DC2626", color: "#DC2626" }}
                  data-testid={`admin-partner-${p.id}-revoke`}>
            Revoke
          </button>
        )}
      </div>
      {expanded && (
        <div className="border-t p-5" style={{ borderColor: "rgba(0,0,0,0.06)" }}
             data-testid={`admin-partner-${p.id}-analytics`}>
          {loading ? (
            <div className="text-sm opacity-60 inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading analytics…
            </div>
          ) : analytics ? (
            <PartnerAnalytics data={analytics} />
          ) : (
            <div className="text-sm opacity-60">No analytics yet.</div>
          )}
        </div>
      )}
    </div>
  );
}

function PartnerAnalytics({ data }) {
  const cats = Object.entries(data.category_mix || {}).sort((a, b) => b[1] - a[1]);
  const total = data.total_pushes || 0;
  const daily = data.daily_pushes || [];
  const maxDaily = daily.reduce((m, r) => Math.max(m, r.count), 0) || 1;
  const status = data.status_counts || {};
  const partnerId = data.partner?.id;
  const [webhooks, setWebhooks] = useState(null);
  const [whLoading, setWhLoading] = useState(false);

  const loadWebhooks = useCallback(async () => {
    if (!partnerId || webhooks) return;
    setWhLoading(true);
    try {
      const { data } = await http.get(`/partners/${partnerId}/webhook-deliveries?limit=25`);
      setWebhooks(data);
    } catch { /* ignore */ }
    finally { setWhLoading(false); }
  }, [partnerId, webhooks]);
  useEffect(() => { loadWebhooks(); }, [loadWebhooks]);

  const deliveryPillColor = {
    delivered: "#065F46", sent: "#065F46",
    queued: "#B45309", retried: "#B45309",
    abandoned: "#991B1B", dropped: "#991B1B", client_error: "#991B1B",
  };

  return (
    <div className="space-y-5">
      {/* Top stats */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label="Total pushes" value={total.toLocaleString()} tint="#059669" />
        <Stat label="Approved"    value={(status.approved || 0).toLocaleString()} tint="#065F46" />
        <Stat label="Pending"     value={(status.pending || 0).toLocaleString()}  tint="#B45309" />
      </div>

      {/* Category mix */}
      <div>
        <div className="text-xs font-mono uppercase tracking-widest mb-2 opacity-70">
          Category mix
        </div>
        {cats.length === 0 ? (
          <div className="text-xs opacity-60">No pushes yet.</div>
        ) : (
          <div className="space-y-1.5" data-testid="admin-partner-analytics-cats">
            {cats.map(([k, v]) => {
              const pct = total > 0 ? Math.round((v / total) * 100) : 0;
              return (
                <div key={k} className="flex items-center gap-3 text-xs">
                  <div className="w-40 font-mono opacity-80 truncate">{k}</div>
                  <div className="flex-1 h-2 rounded-full overflow-hidden"
                       style={{ background: "#F1F5F9" }}>
                    <div className="h-full"
                         style={{ width: `${pct}%`, background: "#059669" }} />
                  </div>
                  <div className="w-16 text-right tabular-nums">{v} <span className="opacity-60">({pct}%)</span></div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Daily sparkline — last 14 days */}
      <div>
        <div className="text-xs font-mono uppercase tracking-widest mb-2 opacity-70">
          Last 14 days
        </div>
        <div className="flex items-end gap-1 h-16" data-testid="admin-partner-analytics-spark">
          {daily.map((d) => {
            const h = Math.max(2, Math.round((d.count / maxDaily) * 60));
            return (
              <div key={d.date}
                   title={`${d.date} — ${d.count}`}
                   className="flex-1 rounded-t"
                   style={{ height: `${h}px`, background: d.count ? "#059669" : "#E2E8F0" }} />
            );
          })}
        </div>
        <div className="mt-1 text-[10px] opacity-60 flex justify-between font-mono">
          <span>{daily[0]?.date}</span>
          <span>{daily[daily.length - 1]?.date}</span>
        </div>
      </div>

      {/* Webhook history */}
      <div data-testid="admin-partner-webhooks">
        <div className="text-xs font-mono uppercase tracking-widest mb-2 opacity-70 flex items-center gap-2">
          Webhook deliveries
          {webhooks?.counts && Object.entries(webhooks.counts).map(([k, v]) => (
            <span key={k} className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                  style={{ background: "#F1F5F9", color: deliveryPillColor[k] || "#0F172A" }}>
              {v} {k}
            </span>
          ))}
        </div>
        {whLoading ? (
          <div className="text-xs opacity-60 inline-flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading webhook history…
          </div>
        ) : !webhooks || webhooks.deliveries.length === 0 ? (
          <div className="text-xs opacity-60">No webhook deliveries yet.</div>
        ) : (
          <div className="rounded-lg border overflow-hidden"
               style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <table className="w-full text-xs">
              <thead style={{ background: "#F9FAFB" }}>
                <tr>
                  <th className="text-left px-3 py-1.5 font-semibold">Event</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Attempt</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Status</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Last try</th>
                </tr>
              </thead>
              <tbody>
                {webhooks.deliveries.slice(0, 15).map((d) => (
                  <tr key={d.id} className="border-t" style={{ borderColor: "rgba(0,0,0,0.05)" }}
                      data-testid={`admin-partner-wh-${d.id}`}>
                    <td className="px-3 py-1.5 font-mono truncate max-w-[180px]">{d.event}</td>
                    <td className="px-3 py-1.5 tabular-nums">#{d.attempt || 1}</td>
                    <td className="px-3 py-1.5">
                      <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold"
                            style={{
                              background: "#F1F5F9",
                              color: deliveryPillColor[d.status] || "#0F172A",
                            }}>
                        {d.status}
                        {d.last_status?.http_status ? ` · ${d.last_status.http_status}` : ""}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 opacity-60">
                      {(d.resolved_at || d.next_retry_at || d.created_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tint }) {
  return (
    <div className="rounded-xl border p-3"
         style={{ borderColor: "rgba(0,0,0,0.06)", background: "#F9FAFB" }}>
      <div className="text-xs opacity-60">{label}</div>
      <div className="mt-1 text-2xl font-display font-bold tabular-nums" style={{ color: tint || "#0F172A" }}>
        {value}
      </div>
    </div>
  );
}

function CopyRow({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="flex items-center gap-2 rounded-lg border p-2 font-mono text-xs"
         style={{ borderColor: "rgba(0,0,0,0.12)", background: "#0F172A", color: "#F8FAFC" }}
         data-testid={testid}>
      <code className="flex-1 truncate">{value}</code>
      <button onClick={copy} className="inline-flex items-center gap-1 text-xs opacity-80 hover:opacity-100">
        {copied ? <ClipboardCheck className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="py-16 text-center opacity-60" data-testid="admin-partners-empty">
      {label}
    </div>
  );
}

function NotAuthorized({ reason }) {
  return (
    <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="admin-partners-not-authorized">
      <Shield className="w-10 h-10 mx-auto mb-3 opacity-50" />
      <h1 className="font-display text-2xl">Not authorised</h1>
      <p className="opacity-70 mt-2 text-sm">
        {reason === "signin" ? "Please sign in as an admin to access this dashboard."
                             : "Your account doesn't have platform-admin privileges."}
      </p>
    </div>
  );
}
